import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {CookieService} from 'ngx-cookie-service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {JwtHelperService} from '@auth0/angular-jwt';
import {catchError, delay, retry, share, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {UserLoggedPermissionsService} from './user-logged-permissions.service';
import {UserLoggedRolesServiceService} from './user-logged-roles-service.service';
import {UserLogged} from '../../models/userLogged';
import {TechnicalPopService} from '../../services/technical-pop.service';

const suffix = environment.subCookiesDomain;
const LOCAL_STORAGE_TOKEN_NAME = 'token' + suffix;
const LOCAL_STORAGE_REFRESH_TOKEN_NAME = 'refreshToken' + suffix;
const LOCAL_STORAGE_REMEMBER_ME_NAME = 'rememberMe' + suffix;
const LOCAL_STORAGE_USER_NAME = 'currentUser' + suffix;
const USER_BW_GRAPH_Key = 'userBwGraphPref';
const USER_HEARTBEAT_PLR_GRAPH_Key = 'userHeartbeatPlrGraphPref';
const USER_QRD_MLR_GRAPH_Key = 'userQrdMlrGraphPref';
const USER_QED_MD_GRAPH_Key = 'userQedMdGraphPref';

@Injectable()
export class AuthenticationService {
  private readonly debugAuthCalls = false;
  private isLoadingSubject$$ = new BehaviorSubject<any>(false);
  private decoder = new JwtHelperService();
  private previousAuthUserForNotification: UserLogged;
  private authenticationEventsSubject$$ = new BehaviorSubject<any>(null);
  private refreshTokenEventsSubject$$ = new BehaviorSubject<any>(null);
  isLoginMode = true;
  displayChangePasswordAlert = false;
  userAuthenticated: UserLogged = null;
  roles: string[] = [];
  userImpersonatedId: string = null;
  userCurrent: UserLogged = null;
  authenticationEvents$: Observable<any> = this.authenticationEventsSubject$$.asObservable();
  isLoading = false;
  isLoading$ = this.isLoadingSubject$$.pipe(delay(0));
  numberOfActiveUsers = 0;
  numberOfTotalUsers = 0;
  redirectUrl: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService,
    public utilsService: UtilsService,
    private userLoggedRolesServiceService: UserLoggedRolesServiceService,
    private userLoggedPermissionsService: UserLoggedPermissionsService,
    private technicalPopService: TechnicalPopService,
  ) {
    if (environment.production) {
      this.debugAuthCalls = false;
    }
    const userFromLocalStorage = this.utilsService.getUserData('currentUser' + environment.subCookiesDomain);
    if (userFromLocalStorage) {
      this.userAuthenticated = userFromLocalStorage;
      if (!this.userCurrent) {
        this.userCurrent = userFromLocalStorage;
      }
    }
    setInterval(() => {
      this.checkTokenValidity();
    }, 1000 * 60 * 10);
    setTimeout(() => {
      this.tryReAuthenticateAutomatically();
    }, 100);
  }

  checkEmailAndTokenValidity(token, isResetPassword = false) {
    return this.http.post<any>(environment.baseUrlWs + '/check-token-validity',
      {'token': token, 'isResetPassword': isResetPassword});
  }

  changeEmailConfirmation(token) {
    return this.http.get<any>(environment.baseUrlWs + '/confirm-email-change/' + token);
  }

  resetPasswordWithToken(token, password, isResetPassword = false) {
    return this.http.post<any>(environment.baseUrlWs + '/reset-password-confirmation',
      {'token': token, 'new_password': password, 'isResetPassword': isResetPassword});
  }

  public checkTokenValidity() {
    const isTokenExpired = this.isTokenExpired();
    if (!isTokenExpired) {
      return true;
    }

    const refreshTokenFromStorage = this.getRefreshTokenFromStorage();
    if (refreshTokenFromStorage) {
      this.tryReAuthenticateAutomatically();
    }
  }

  public tryReAuthenticateAutomatically() {
    const refreshTokenFromStorage = this.getRefreshTokenFromStorage();
    if (!refreshTokenFromStorage) {
      return;
    }
    this.refreshTokenUsingWs$().subscribe({
      error: () => {
        this.logoutUser();
      }
    });
  }

  private getTokenFromStorage(): string | null {
    return this.utilsService.getCookie('token');
  }

  private getRefreshTokenFromStorage(): string | null {
    return this.utilsService.getCookie(LOCAL_STORAGE_REFRESH_TOKEN_NAME);
  }

  public isTokenExpired(): boolean {
    const token = this.getTokenFromStorage();
    if (!token) {
      return true;
    }
    return this.decoder.isTokenExpired(token);
  }

  public getDecodedToken(): boolean {
    const token = this.getTokenFromStorage();
    if (!token) {
      return null;
    }
    return this.decoder.decodeToken(token);
  }

  public getLocalValidTokenOrGetANewOneFromWs$(): Observable<any> {
    const token = this.getTokenFromStorage();
    const decoder = new JwtHelperService();
    if (token && !decoder.isTokenExpired(token)) {
      return of(token);
    }
    return this.refreshTokenUsingWs$();
  }

  public loginUsingWs$(login, password, rememberMe): Observable<any | null> {
    const loginUserData = {'_email': login, '_password': password};
    this.isLoading = true;
    this.isLoadingSubject$$.next(true);
    const loginQuery$ = this.http.post<any>(environment.baseUrlWs + '/login_check', loginUserData, {withCredentials: true}).pipe(share(), retry(0));
    return loginQuery$.pipe(
      switchMap((response) => {
        this.isLoading = false;
        this.isLoadingSubject$$.next(false);
        this.userCurrent = response.user;
        this.setNumberOfActiveAndTotalUsers();
        response.user.access = response.access;
        response.user.loadedFrom = 'login';
        this.makePermissions(response.user);
        this.storeUserDataFromTokens(response, rememberMe);
        if (this.debugAuthCalls) {
          this.utilsService.displayMessage('Auth debug', 'Login success!', 'info');
        }
        this.notifyForUserChanged(this.userCurrent);
        if (!this.userCurrent.permissions.mustChangePassword && !this.userCurrent.permissions.incompleteLogin) {
          if (this.redirectUrl) {
            this.utilsService.navigate(this.redirectUrl);
          } else {
            this.utilsService.navigate('');
          }
        }
        if (environment.displayCageNameForClient === true || this.userCurrent.permissions.isMckayUser) {
          this.technicalPopService.loadTooltipData().subscribe();
        }
        return of(response.token);
      }),
      catchError((error) => {
          this.isLoading = false;
          this.isLoadingSubject$$.next(false);
          this.logoutUser();
          if (this.debugAuthCalls) {
            this.utilsService.displayMessage('Auth debug', 'Login error!', 'error');
          }
          return this.utilsService.handleErrorWebservice$(error);
        }
      ));
  }

  confirmGoogleAuthenticatorCode(code) {
    return this.http.post<any>(environment.baseUrlWs + '/api/confirm-authenticator-code', {'code': code}, {withCredentials: true});
  }

  private notifyForUserChanged(user: UserLogged) {
    if (!this.previousAuthUserForNotification || !user || !user.id
      || (this.previousAuthUserForNotification && this.previousAuthUserForNotification.id
        && user.id && user.id !== this.previousAuthUserForNotification.id)) {
      this.previousAuthUserForNotification = user;
      this.authenticationEventsSubject$$.next(user);
    }
  }

  refreshTokenUsingWs$(): Observable<string | null> {
    const refreshTokenUrl = environment.baseUrlWs + '/api/token/refresh';
    const refreshToken = this.getRefreshTokenFromStorage();
    if (!refreshToken) {
      return of(null);
    }

    this.isLoading = true;
    this.isLoadingSubject$$.next(true);
    const sendData = {refresh_token: refreshToken};
    const refreshQuery$ = this.http.post<any>(refreshTokenUrl, sendData, {withCredentials: true}).pipe(share(), retry(0));
    return refreshQuery$.pipe(
      switchMap((response) => {
        if (this.userLoggedRolesServiceService.userHasOneOfThoseRoles(response.user, ['ROLE_INCOMPLETE_LOGIN'])) {
          this.isLoading = false;
          this.isLoadingSubject$$.next(false);
          return of(null);
        }
        this.isLoading = false;
        this.isLoadingSubject$$.next(false);
        response.user.loadedFrom = 'refreshToken';
        const rememberMeFromStorage = this.utilsService.loadUserPref(LOCAL_STORAGE_REMEMBER_ME_NAME);
        this.storeUserDataFromTokens(response, rememberMeFromStorage);
        this.userCurrent = response.user;
        this.setNumberOfActiveAndTotalUsers();
        this.userCurrent.access = response.access;
        this.makePermissions(this.userCurrent);
        const impersonatedUser = this.utilsService.getCookie('impersonatedUser');
        if (impersonatedUser) {
          return this.impersonateUserUsingWs$(impersonatedUser);
        }
        if (environment.displayCageNameForClient === true || this.userCurrent.permissions.isMckayUser) {
          this.technicalPopService.loadTooltipData().subscribe();
        }
        this.notifyForUserChanged(this.userCurrent);
        if (this.debugAuthCalls) {
          this.utilsService.displayMessage('Auth debug', 'Refresh token success!', 'info');
        }
        this.refreshTokenEventsSubject$$.next(this.userCurrent);
        return of(response.token);
      }),
      catchError((error) => {
          this.logoutUser();
          this.isLoading = false;
          this.isLoadingSubject$$.next(false);
          this.utilsService.unlockUi();
          return throwError(error);
        }
      ));
  }

  public impersonateUserUsingWs$(userImpersonateMail): Observable<any | null> {
    this.removeUserGraphPref();
    this.removeUserFilterPref();
    this.userImpersonatedId = userImpersonateMail;
    const dataToPost = {user_to_impersonate: userImpersonateMail};
    this.utilsService.lockUi();
    const impersonateQuery$ = this.http.post<any>(environment.baseUrlWs
      + '/user/impersonate-user', dataToPost).pipe(share(), retry(0));
    return impersonateQuery$.pipe(
      switchMap((response) => {
        response.user = response.logged_in_user;
        response.user.loadedFrom = 'impersonate';
        response.user.access = response.access;
        this.makePermissions(response.user);
        this.userCurrent = response.logged_in_user;
        this.setNumberOfActiveAndTotalUsers();
        this.userCurrent.access = response.access;
        this.notifyForUserChanged(this.userCurrent);
        this.utilsService.unlockUi();
        const alreadyImpersonated = !!this.utilsService.getCookie('impersonatedUser');
        this.utilsService.saveCookie('impersonatedUser', userImpersonateMail, this.getImpersonateTokenExpirationTime());
        if (environment.displayCageNameForClient === true || this.userCurrent.permissions.isMckayUser) {
          this.technicalPopService.loadTooltipData().subscribe();
        }
        if (!alreadyImpersonated) {
          this.utilsService.navigate('');
        }
        return of(response);
      }),
      catchError((error) => {
          this.userImpersonatedId = null;
          this.cookieService.delete('impersonatedUser', '/');
          this.utilsService.unlockUi();
          const title = error.error?.title || 'Impersonation failed';
          const message = error.error?.description
            || (error.status === 401 ? 'You are not allowed to impersonate this user.' : 'Could not impersonate this user.');
          this.utilsService.displayMessage(title, message, 'error');
          return throwError(error);
        }
      ));
  }

  private getImpersonateTokenExpirationTime() {
    if (!this.utilsService.getUserData('loginUserDate') || this.utilsService.getUserData('loginUserDate').split('T').length === 0) {
      return null;
    }
    const date = new Date(this.utilsService.getUserData('loginUserDate').split('T')[0]);
    const newMonth = date.getMonth() + 1; // JavaScript's months are 0-11
    date.setMonth(newMonth);
    // Handle cases where adding a month rolls the date to the next month
    if (date.getMonth() !== (newMonth % 12)) {
      date.setDate(0);
    }
    return date;
  }

  public stopImpersonateUser(navigateHome = true) {
    this.removeUserGraphPref();
    this.removeUserFilterPref();
    this.userImpersonatedId = null;
    this.setImpersonateCookieWithAnOldDateOrRemoveIt();
    this.userCurrent = this.userAuthenticated;
    this.setNumberOfActiveAndTotalUsers();
    this.utilsService.removeUserData(LOCAL_STORAGE_USER_NAME);
    this.makePermissions(this.userCurrent);
    this.notifyForUserChanged(this.userCurrent);
    if (navigateHome) {
      this.router.navigate(['']);
    }
    this.utilsService.unlockUi();
  }

  private setImpersonateCookieWithAnOldDateOrRemoveIt() {
    // In some browsers deleting the cookie doesn't work, so we set the cookie expiration date in the past.
    const date = new Date();
    date.setDate(date.getDate() - 1);
    const userImpersonateMail = this.utilsService.getCookie('impersonatedUser');
    this.cookieService.set('impersonatedUser' + environment.subCookiesDomain, userImpersonateMail, date, '/', environment.cookieDomain, true, 'None');
    this.cookieService.set('impersonatedUser' + environment.subCookiesDomain, userImpersonateMail, date, '/', environment.cookieDomain, null, null);
    this.utilsService.deleteCookie('impersonatedUser');
  }

  private removeUserGraphPref() {
    this.utilsService.removeUserData(USER_BW_GRAPH_Key);
    this.utilsService.removeUserData(USER_HEARTBEAT_PLR_GRAPH_Key);
    this.utilsService.removeUserData(USER_QRD_MLR_GRAPH_Key);
    this.utilsService.removeUserData(USER_QED_MD_GRAPH_Key);
  }

  private removeUserFilterPref() {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.indexOf('Filter') !== -1) {
        this.utilsService.removeUserData(key);
      }
    }
  }

  private setNumberOfActiveAndTotalUsers() {
    this.numberOfActiveUsers = (this.userCurrent) ? this.userCurrent.company.activeUsers : 0;
    this.numberOfTotalUsers = (this.userCurrent) ? this.userCurrent.company.totalUsers : 0;
  }

  private storeUserDataFromTokens(response, rememberMe = false) {
    this.userAuthenticated = response.user;
    let expireDate = null;
    if (rememberMe === true) {
      expireDate = new Date();
      expireDate.setMonth(expireDate.getMonth() + 1);
    }
    // cookies
    this.cookieService.set(LOCAL_STORAGE_TOKEN_NAME, response.token, expireDate, '/', environment.cookieDomain, true, 'None');
    if (!this.cookieService.get(LOCAL_STORAGE_TOKEN_NAME) || this.isTokenExpired()) {
      this.cookieService.set(LOCAL_STORAGE_TOKEN_NAME, response.token, expireDate, '/', environment.cookieDomain, null, null);
    }
    this.utilsService.saveCookie(LOCAL_STORAGE_REFRESH_TOKEN_NAME, response.refresh_token, expireDate);
    this.utilsService.saveCookie(LOCAL_STORAGE_REMEMBER_ME_NAME, String(rememberMe), expireDate);
    this.utilsService.saveUserData(LOCAL_STORAGE_TOKEN_NAME, response.token);
    this.utilsService.saveUserData(LOCAL_STORAGE_REFRESH_TOKEN_NAME, response.refresh_token);
    this.utilsService.saveUserData(LOCAL_STORAGE_REMEMBER_ME_NAME, rememberMe);
    this.utilsService.saveUserData('loginUserDate', JSON.stringify(new Date()));
    return response.token;
  }

  public logoutUser(navigateHome = true) {
    this.stopImpersonateUser(navigateHome);
    this.userAuthenticated = null;
    this.userImpersonatedId = null;
    this.userCurrent = null;
    this.roles = [];
    const date = new Date();
    date.setDate(date.getDate() - 1);
    this.utilsService.saveCookie(LOCAL_STORAGE_TOKEN_NAME, null, date);
    this.utilsService.deleteCookie(LOCAL_STORAGE_TOKEN_NAME);
    this.utilsService.deleteCookie(LOCAL_STORAGE_REFRESH_TOKEN_NAME);
    this.utilsService.deleteCookie(LOCAL_STORAGE_REMEMBER_ME_NAME);
    this.removeUserLocalStorageData();
    this.utilsService.saveUserData('logoutUserDate', JSON.stringify(new Date()));
    this.notifyForUserChanged(null);
  }

  private removeUserLocalStorageData() {
    this.utilsService.removeUserData(LOCAL_STORAGE_TOKEN_NAME);
    this.utilsService.removeUserData(LOCAL_STORAGE_REFRESH_TOKEN_NAME);
    this.utilsService.removeUserData(LOCAL_STORAGE_USER_NAME);
    this.utilsService.removeUserData(LOCAL_STORAGE_USER_NAME);
    this.removeUserGraphPref();
    this.removeUserFilterPref();
  }

  public waitForAuthenticationThenLaunch<T>(functionToLaunch: () => T) {
    let currentUserAuth = null;
    return this.authenticationEvents$.subscribe(
      (userAuth) => {
        if (userAuth !== currentUserAuth) {
          currentUserAuth = userAuth;
          if (userAuth) {
            functionToLaunch();
          }
        }
      });
  }

  public userHasOneOfThosePermissions(permissionNames: string[]) {
    return this.userCurrent ? this.userLoggedPermissionsService.userHasOneOfThosePermissions(this.userCurrent, permissionNames) : false;
  }

  public userHasAllOfThosePermissions(permissionNames: string[]) {
    return this.userCurrent ? this.userLoggedPermissionsService.userHasAllOfThosePermissions(this.userCurrent, permissionNames) : false;
  }

  public userHasOneOfThoseRoles(rolesNames: string[]) {
    return this.userCurrent ? this.userLoggedRolesServiceService.userHasOneOfThoseRoles(this.userCurrent, rolesNames) : false;
  }

  public userHasRole(roleName: string) {
    return this.userCurrent ? this.userLoggedRolesServiceService.userHasRole(this.userCurrent, roleName) : false;
  }

  private makePermissions(responseUser: any) {
    this.userLoggedPermissionsService.calculateUserPermissions(responseUser);
  }

  getUserCurrent() {
    if (!this.userCurrent) {
      return this.userAuthenticated;
    }
    return this.userCurrent;
  }
}
